import type { GameContext } from "../../GameContext";

export interface CombatTarget {
  entityId: string;
  name: string;
  hp: number;
  maxHp: number;
}

/**
 * Always-on overlay (visibility toggled internally, not via WorldHud's
 * panel buttons) showing the current target's HP and a skill bar. Skill
 * numbers themselves (cooldowns, damage) are never computed here -- per
 * 1.4, StatCalculator.ts is server-authoritative; this only sends intent
 * (`use_skill`) and renders whatever entity_update comes back.
 */
export class CombatHud {
  private el: HTMLDivElement;
  private targetNameEl: HTMLDivElement;
  private hpBarFillEl: HTMLDivElement;
  private target: CombatTarget | null = null;

  constructor(root: HTMLElement, private readonly ctx: GameContext) {
    this.el = document.createElement("div");
    this.el.className = "combat-hud";
    this.el.style.display = "none";

    this.targetNameEl = document.createElement("div");
    this.targetNameEl.className = "combat-target-name";
    this.el.appendChild(this.targetNameEl);

    const hpBarFrame = document.createElement("div");
    hpBarFrame.className = "combat-hp-bar-frame";
    const hpBarTrack = document.createElement("div");
    hpBarTrack.className = "combat-hp-bar-track";
    this.hpBarFillEl = document.createElement("div");
    this.hpBarFillEl.className = "combat-hp-bar-fill";
    hpBarTrack.appendChild(this.hpBarFillEl);
    hpBarFrame.appendChild(hpBarTrack);
    this.el.appendChild(hpBarFrame);

    const skillBar = document.createElement("div");
    skillBar.className = "combat-skill-bar";
    for (let slot = 1; slot <= 4; slot++) {
      const btn = document.createElement("button");
      btn.textContent = String(slot);
      btn.addEventListener("click", () => this.useSkill(`slot_${slot}`));
      skillBar.appendChild(btn);
    }
    this.el.appendChild(skillBar);

    root.appendChild(this.el);
  }

  setTarget(target: CombatTarget | null): void {
    this.target = target;
    this.el.style.display = target ? "block" : "none";
    if (!target) return;
    this.targetNameEl.textContent = target.name;
    this.updateHpDisplay(target.hp, target.maxHp);
  }

  updateHpDisplay(hp: number, maxHp: number): void {
    const ratio = maxHp > 0 ? Math.max(0, Math.min(1, hp / maxHp)) : 0;
    this.hpBarFillEl.style.width = `${ratio * 100}%`;
  }

  private useSkill(skillSlotId: string): void {
    if (!this.target) return;
    // Client sends INTENT only -- server validates and resolves the
    // outcome (combat-do, per 2.8/2.12), then broadcasts entity_update.
    this.ctx.network.send({
      type: "use_skill",
      payload: { skillSlotId, targetEntityId: this.target.entityId },
    });
  }
}
